import "../../stylesheets/about-package/about.less";
import "../../stylesheets/grids-package/grids.less";
import "../../stylesheets/mobile-package/mobile.less";
import "../../stylesheets/shared-package/shared.less";

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {doNotTranslate} from "@khan/i18n";
import {staticUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import DonateButton from "#page-package/donate-button.tsx";

import AboutHeader from "./about-header.tsx";
import AboutNav from "./about-nav.tsx";
import {withStrings} from "./locales/about/load-strings.tsx";

type OwnProps = Empty;
type Props = OwnProps & WithActionSchedulerProps;
type State = {
    visibleTestimonialIndex: number;
};

class About extends React.Component<Props, State> {
    testimonials: Array<{
        bgColor: string;
        text: string;
        firstName: string;
    }>;

    constructor(props: Props) {
        super(props);
        this.state = {
            visibleTestimonialIndex: 0,
        };
        this.testimonials = [
            {
                bgColor: "#54C0A6",
                text: t`I just found your web site. I am 72 years old and I am now taking up learning where I left off. Thank you so much for all your hard work. `,
                firstName: doNotTranslate`Barbara`,
            },
            {
                bgColor: "#3A8EA2",
                text: t`Thank you for your ongoing inspiration. I am a primary school teacher in the north of England and when the conventional education system gets on my nerves, I only need to whack on a Khan Academy and my faith in the future of education is restored!`,
                firstName: doNotTranslate`Kimberly`,
            },
            {
                bgColor: "#8D4B9A",
                text: t`Dear Khan Academy, I greatly appreciate your videos! Not only did I pass Calculus last year with a 96%, but I also discovered my love for the subject - something I had never experienced before truly understanding the mathematical content and the uses of it.`,
                firstName: doNotTranslate`Matt`,
            },
        ];
    }

    testimonialTransitionInterval = this.props.schedule.interval(() => {
        const testimonialsRef = this.testimonialsRef.current;
        if (!testimonialsRef) {
            return null;
        }

        // This conditional is a workaround for a bug when the testimonial
        // rerenders, it causes the "Sign up to test our AI-powered guide,
        // Khanmigo." banner to flicker/rerender. This is a jarring experience
        // so we only want to change the testimonial if the user is looking at
        // the testimonials section. Other top banners may have this issue as
        // well.
        if (
            testimonialsRef.offsetTop > window.scrollY &&
            testimonialsRef.offsetTop + testimonialsRef.offsetHeight <
                window.scrollY + window.innerHeight
        ) {
            this.changeTestimonial(1);
        }
    }, 4000);
    testimonialsRef = React.createRef<HTMLDivElement>();

    // delta is the number of slides to move. e.g. 1 moves forward one slide,
    // -2 moves back two slides.
    changeTestimonial(delta: number) {
        this.setState({
            visibleTestimonialIndex:
                Math.abs(this.state.visibleTestimonialIndex + delta) %
                this.testimonials.length,
        });
    }

    renderHeadingOne: () => React.ReactNode = () => (
        <div className="about-shelf">
            <div className="content">
                <h2>{t`A personalized learning resource for all ages`}</h2>
                <p>
                    {t`With practice exercises, instructional videos, and a personalized learning dashboard, Khan Academy empowers learners to study at their own pace, both in and out of the classroom. Covering subjects from kindergarten through early college, including math, science, reading, computing, history, art history, economics, financial literacy, SAT, MCAT, and more, Khan Academy focuses on skill mastery to help learners build the strong foundations they need to succeed in higher education and in their careers.`}
                </p>
                <div id="kui-button-1" className="center-button" />
            </div>
        </div>
    );

    renderHeadingTwo: () => React.ReactNode = () => (
        <div className="about-shelf">
            <div className="content">
                <h2>{t`Free tools for parents and teachers`}</h2>
                <p>
                    {t`We’re working hard to ensure that Khan Academy empowers coaches of all kinds to better understand what their children or students are up to and how best to help them. See at a glance whether a child or student is struggling or if she hit a streak and is now far ahead of the class. Our teacher dashboard provides a summary of class performance as a whole as well as detailed student profiles.`}
                </p>
                <div id="coaching-button" className="center-button">
                    <Button href="/resources" style={styles.btn}>
                        {t`Learn more about coaching`}
                    </Button>
                </div>
            </div>
        </div>
    );

    renderHeadingThree: () => React.ReactNode = () => (
        <div className="about-shelf">
            <div className="content">
                <h2>{t`From humble beginnings to a world-class team`}</h2>
                <p>
                    {t`What started as one man tutoring his cousin has grown into a more than 150-person organization. We’re a diverse team that has come together to work on an audacious mission: to provide a free world-class education for anyone, anywhere. We are developers, teachers, designers, strategists, scientists, and content specialists who passionately believe in inspiring the world to learn. A few great people can make a big difference.`}
                </p>
                <div id="team-button" className="center-button">
                    <Button style={styles.btn} href="/about/the-team">
                        {t`Learn more about our team`}
                    </Button>
                </div>
            </div>
        </div>
    );

    renderUpperImageRow: () => React.ReactNode = () => {
        // TODO(davemcalpine): make these repeated background images, they're
        // just for decoration
        const images = [
            "/images/about/product_images/volume5.gif",
            "/images/about/product_images/about_withgifs-07.jpg",
            "/images/about/product_images/about_withgifs-12.jpg",
            "/images/about/product_images/volume5.gif",
            "/images/about/product_images/about_withgifs-07.jpg",
            "/images/about/product_images/about_withgifs-12.jpg",
            "/images/about/product_images/volume5.gif",
            "/images/about/product_images/about_withgifs-07.jpg",
        ];

        return (
            <div className="about-shelf image-row teal">
                {images.map((image, i) => (
                    <img key={i} src={staticUrl(image)} alt="" />
                ))}
            </div>
        );
    };

    renderLowerImageRow: () => React.ReactNode = () => {
        // TODO(davemcalpine): make these repeated background images, they're
        // just for decoration
        const images = [
            "/images/about/product_images/about_withgifs-09.jpg",
            "/images/about/product_images/about_withgifs-13.jpg",
            "/images/about/product_images/graph.gif",
            "/images/about/product_images/about_withgifs-10.jpg",
            "/images/about/product_images/about_withgifs-09.jpg",
            "/images/about/product_images/about_withgifs-13.jpg",
            "/images/about/product_images/graph.gif",
            "/images/about/product_images/about_withgifs-10.jpg",
            "/images/about/product_images/about_withgifs-09.jpg",
            "/images/about/product_images/about_withgifs-13.jpg",
        ];

        return (
            <div className="about-shelf image-row blue">
                {images.map((image, i) => (
                    <img key={i} src={staticUrl(image)} alt="" />
                ))}
            </div>
        );
    };

    renderLanguages: () => React.ReactNode = () => {
        return (
            <div className="about-shelf">
                <div className="content">
                    <h2>{t`You’re joining a global classroom`}</h2>
                    <p>
                        <Trans>
                            Millions of students from all over the world, each
                            with their own unique story, learn at their own pace
                            on Khan Academy every single day. Our resources are
                            being translated into more than 36 languages in
                            addition to the{" "}
                            <a href="https://es.khanacademy.org/">Spanish</a>,{" "}
                            <a href="https://fr.khanacademy.org/">French</a>,
                            and{" "}
                            <a href="https://pt.khanacademy.org/">
                                Brazilian Portuguese
                            </a>{" "}
                            versions of our site.
                        </Trans>
                    </p>
                    <div id="stories-button" className="center-button">
                        <Button href="/stories" style={styles.btn}>
                            {t`Learn more about their stories`}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    renderTestimonials: () => React.ReactNode = () => (
        <div
            className="about-shelf testimonials"
            style={{
                backgroundColor:
                    this.testimonials[this.state.visibleTestimonialIndex]
                        .bgColor,
            }}
            ref={this.testimonialsRef}
        >
            <div className="content">
                <button
                    className="icon-button icon-button-left"
                    onClick={() => {
                        this.changeTestimonial(-1);
                        this.testimonialTransitionInterval.clear();
                    }}
                >
                    <i
                        className="icon-chevron-left navigation-arrow"
                        aria-label={t`Previous testimonial`}
                    />
                </button>
                <ul aria-live="polite">
                    {this.testimonials.map((testimonial, i) => {
                        if (i === this.state.visibleTestimonialIndex) {
                            return (
                                <li
                                    key={i}
                                    className="testimonial"
                                    style={{
                                        backgroundColor: testimonial.bgColor,
                                    }}
                                >
                                    <blockquote>{testimonial.text}</blockquote>
                                    <cite>{testimonial.firstName}</cite>
                                </li>
                            );
                        }
                    })}
                </ul>
                <button
                    className="icon-button icon-button-right"
                    onClick={() => {
                        this.changeTestimonial(1);
                        this.testimonialTransitionInterval.clear();
                    }}
                >
                    <i
                        className="icon-chevron-right navigation-arrow"
                        aria-label={t`Next testimonial`}
                    />
                </button>
            </div>
        </div>
    );

    renderDonationShelf: () => React.ReactNode = () => (
        <div className="about-shelf donation-shelf">
            <div className="content">
                <h2>{t`Anyone can learn anything.  For free.`}</h2>
                <p>
                    {t`Education is a human right.  We are a nonprofit because we believe in a free, world-class education for anyone, anywhere.  Instead of ads or subscriptions, we are supported by individual contributions from people like you.  Please join us today.`}
                </p>
                <div id="donate-button" className="center-button">
                    <DonateButton
                        style={styles.btn}
                        urlParams={{c_src2: "Webapp.about"}}
                        light={true}
                    />
                </div>
            </div>
        </div>
    );

    render(): React.ReactNode {
        return (
            <PageRoot settings={{title: t`About`}}>
                <div className="about-page">
                    <AboutHeader
                        className="header-with-background-video"
                        pageheadline={t`Our mission is to provide a free, world‑class education for anyone, anywhere.`}
                        backgroundVideo={
                            // Snippets of a series of khan academy video lessons.
                            <video
                                className="responsive-video"
                                loop
                                muted={true}
                                autoPlay
                            >
                                <source
                                    src={staticUrl(
                                        "/videos/homepage-background.mp4",
                                    )}
                                    type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"'
                                />
                                <source
                                    src={staticUrl(
                                        "/videos/homepage-background.webm",
                                    )}
                                    type='video/webm; codecs="vp8, vorbis"'
                                />
                                <source
                                    src={staticUrl(
                                        "/videos/homepage-background.ogv",
                                    )}
                                    type='video/ogv; codecs="theora, vorbis"'
                                />
                            </video>
                        }
                    />
                    <AboutNav aboutSelection="about" />
                    {this.renderHeadingOne()}
                    {this.renderUpperImageRow()}
                    {this.renderHeadingTwo()}
                    {this.renderLowerImageRow()}
                    {this.renderLanguages()}
                    {this.renderTestimonials()}
                    {this.renderHeadingThree()}
                    {this.renderDonationShelf()}
                </div>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    btn: {
        minWidth: 280,
    },
});

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof About,
        React.ComponentProps<typeof About>
    >
>;
export default withStrings(
    withActionScheduler(About) as React.ComponentType<ExportProps>,
);
